import React, { useState } from 'react';
import { Box, IconButton, TextField, Typography, CircularProgress } from '@mui/material';
import { Check, Close, Edit } from '@mui/icons-material';
import { Device, deviceComment, displayName } from '../types';
import { deviceApi } from '../api';
import { useI18n } from '../i18n';

interface DeviceNameEditorProps {
  device: Device;
  variant?: 'h4' | 'h6' | 'body1' | 'subtitle1';
  onUpdated?: (device: Device) => void;
}

const DeviceNameEditor: React.FC<DeviceNameEditorProps> = ({
  device,
  variant = 'h6',
  onUpdated,
}) => {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const title = displayName(device);
  const comment = deviceComment(device);

  const startEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setDraft(comment);
    setEditing(true);
  };

  const cancel = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    event?.preventDefault();
    setEditing(false);
    setDraft('');
  };

  const save = async (event?: React.FormEvent | React.MouseEvent) => {
    event?.stopPropagation();
    event?.preventDefault();
    if (!device.id) return;
    setSaving(true);
    try {
      const updated = await deviceApi.updateDevice(device.id, { name: draft.trim() });
      onUpdated?.(updated);
      setEditing(false);
    } catch (err) {
      console.error('Failed to save device comment', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant={variant} component="div" noWrap title={title}>
        {title}
      </Typography>
      {editing ? (
        <Box
          component="form"
          onClick={(e) => e.stopPropagation()}
          onSubmit={save}
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{ minWidth: 0 }}
        >
          <TextField
            size="small"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') cancel();
            }}
            placeholder={t('common.comment')}
            disabled={saving}
            sx={{ minWidth: 140, flex: 1 }}
          />
          <IconButton size="small" color="primary" onClick={save} disabled={saving} title={t('common.saveComment')}>
            {saving ? <CircularProgress size={16} /> : <Check fontSize="small" />}
          </IconButton>
          <IconButton size="small" onClick={cancel} disabled={saving} title={t('common.cancel')}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Box display="flex" alignItems="center" gap={0.25} sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            title={comment || t('common.comment')}
            sx={{ fontStyle: comment ? 'normal' : 'italic' }}
          >
            {comment || t('common.comment')}
          </Typography>
          <IconButton size="small" onClick={startEdit} title={t('common.editComment')} sx={{ flexShrink: 0 }}>
            <Edit fontSize="inherit" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default DeviceNameEditor;
